import './styles.css';

type RGB = { r:number; g:number; b:number };
type Drop = { before:RGB; paint:number; stage:number; drops:number; score:number };
type Game = {
  seed:string;
  stage:number;
  drops:number;
  totalScore:number;
  pendingPoints:number;
  pendingComboBefore:number;
  best:number;
  combo:number;
  lastResult:string;
  lastOutcome:''|'clear'|'over';
  target:RGB;
  mix:RGB;
  history:Drop[];
  finished:boolean;
};

const app = document.querySelector<HTMLDivElement>('#app')!;

const paints = [
  { key:'red', label:'RED', color:{ r:238, g:62, b:86 } },
  { key:'yellow', label:'YELLOW', color:{ r:255, g:202, b:54 } },
  { key:'blue', label:'BLUE', color:{ r:54, g:118, b:244 } },
] as const;

const MAX_STAGE = 5;
const MAX_DROPS = 6;
const START:RGB = { r:246, g:238, b:218 };
let audio:AudioContext|null = null;
let clearTimer:number|undefined;
let game = createGame(new URLSearchParams(location.search).get('seed') || seedFromDate());

function seedFromDate(){ return Math.floor(Date.now()/86400000).toString(36)+'-'+Math.random().toString(36).slice(2,6); }
function rng(seed:string){ let h=2166136261; for(const c of seed) h=Math.imul(h^c.charCodeAt(0),16777619); return()=>{ h+=h<<13; h^=h>>>7; h+=h<<3; h^=h>>>17; h+=h<<5; return (h>>>0)/4294967296; }; }
function clamp(n:number){ return Math.max(0, Math.min(255, Math.round(n))); }
function css(c:RGB){ return `rgb(${clamp(c.r)} ${clamp(c.g)} ${clamp(c.b)})`; }
function mix(a:RGB,b:RGB,t:number):RGB{ return { r:a.r*(1-t)+b.r*t, g:a.g*(1-t)+b.g*t, b:a.b*(1-t)+b.b*t }; }
function distance(a:RGB,b:RGB){ return Math.hypot(a.r-b.r,a.g-b.g,a.b-b.b)/441.7; }
function score(){ return Math.max(0, 100 - Math.round(distance(game.mix, game.target)*100)); }
function stars(){ return game.best>=92?3:game.best>=84?2:game.best>=74?1:0; }
function dropLabel(match:number,drops:number){
  if(match < 90) return drops >= MAX_DROPS ? 'Close Mix' : 'Try Again';
  if(drops <= 2) return 'Perfect Mix';
  if(drops <= 3) return 'Great Mix';
  if(drops <= 4) return 'Clean Mix';
  return 'Clear Mix';
}
function comboMultiplier(combo:number){ return 1 + Math.max(0, combo-1) * .25; }
function stagePoints(match:number,drops:number,combo:number){ const clearBonus = match>=90 ? 250 : 0; const dropBonus = Math.max(0, MAX_DROPS-drops) * 35; return Math.round((match*10 + clearBonus + dropBonus) * comboMultiplier(combo)); }
function targetFor(seed:string, stage:number):RGB{
  const r = rng(`${seed}:${stage}`);
  let c = paints[Math.floor(r()*paints.length)].color;
  const steps = 2 + Math.floor(r()*3);
  for(let i=0;i<steps;i++) c = mix(c, paints[Math.floor(r()*paints.length)].color, .25 + r()*.45);
  return c;
}
function createGame(seed:string):Game{
  return { seed, stage:1, drops:0, totalScore:0, pendingPoints:0, pendingComboBefore:0, best:0, combo:0, lastResult:'', lastOutcome:'', target:targetFor(seed,1), mix:{...START}, history:[], finished:false };
}
function bootAudio(){ audio ??= new AudioContext(); audio.resume?.(); }
function tone(f:number,d=.055,type:OscillatorType='sine',g=.025){ if(!audio)return; const now=audio.currentTime,o=audio.createOscillator(),gain=audio.createGain(); o.frequency.value=f; o.type=type; gain.gain.setValueAtTime(.0001,now); gain.gain.exponentialRampToValueAtTime(g,now+.01); gain.gain.exponentialRampToValueAtTime(.0001,now+d); o.connect(gain); gain.connect(audio.destination); o.start(now); o.stop(now+d+.02); }
function sound(kind:'drop'|'good'|'undo'|'tap'|'share'){ if(kind==='drop'){tone(520,.045,'triangle',.02); setTimeout(()=>tone(720,.04,'sine',.012),35);} else if(kind==='good'){[523,659,784,1046].forEach((f,i)=>setTimeout(()=>tone(f,.075,'triangle',.025),i*45));} else if(kind==='undo') tone(240,.08,'square',.017); else if(kind==='share') tone(880,.06,'sine',.016); else tone(420,.04,'triangle',.014); }
function vibrate(n=12){ navigator.vibrate?.(n); }

function addPaint(index:number){
  if(game.finished || clearTimer) return;
  bootAudio();
  const before = {...game.mix};
  const beforeScore = score();
  game.history.push({ before, paint:index, stage:game.stage, drops:game.drops, score:beforeScore });
  game.mix = mix(game.mix, paints[index].color, game.drops===0 ? .55 : .32);
  game.drops++;
  const now = score();
  game.best = Math.max(game.best, now);
  sound(now >= beforeScore ? 'good' : 'drop');
  vibrate(now>=90 ? 24 : 10);
  if(now >= 90 || game.drops >= MAX_DROPS){
    finishStage(now);
  } else {
    game.lastResult = '';
    render(`${paints[index].label} drop · ${beforeScore}% → ${now}%`);
  }
}
function finishStage(finalMatch=score()){
  const cleared = finalMatch >= 90;
  game.pendingComboBefore = game.combo;
  game.combo = cleared ? game.combo + 1 : 0;
  const pts = stagePoints(finalMatch, game.drops, game.combo);
  game.pendingPoints = pts;
  game.totalScore += pts;
  const comboText = cleared && game.combo > 1 ? ` · x${game.combo} combo` : '';
  const bonusText = dropLabel(finalMatch, game.drops);
  game.lastOutcome = cleared ? 'clear' : 'over';
  game.lastResult = `${cleared ? 'CLEAR' : 'STAGE OVER'} · ${bonusText} · ${finalMatch}% · ${game.drops} drops · +${pts} pts${comboText}`;
  clearTimer = 1;
  render(game.lastResult);
  if(game.stage >= MAX_STAGE){
    game.finished = true;
    clearTimer = undefined;
    sound('good');
    render('Finished.');
    return;
  }
  clearTimer = window.setTimeout(nextStage, cleared ? 950 : 1100);
}
function nextStage(){
  clearTimer = undefined;
  game.pendingPoints = 0;
  game.pendingComboBefore = game.combo;
  if(game.stage >= MAX_STAGE){
    game.finished = true;
    sound('good');
    render('Finished.');
    return;
  }
  game.stage++;
  game.drops = 0;
  game.target = targetFor(game.seed, game.stage);
  game.mix = {...START};
  game.history = [];
  game.lastResult = '';
  game.lastOutcome = '';
  sound('tap');
  render('New target.');
}
function undo(){
  bootAudio();
  if(clearTimer){ clearTimeout(clearTimer); clearTimer = undefined; game.totalScore = Math.max(0, game.totalScore - game.pendingPoints); game.combo = game.pendingComboBefore; game.pendingPoints = 0; game.lastResult = ''; game.lastOutcome = ''; }
  const last = game.history.pop();
  if(!last){ render('Nothing to undo.'); return; }
  game.mix = last.before;
  game.drops = last.drops;
  game.finished = false;
  sound('undo');
  vibrate(8);
  render('Undo.');
}
function reset(){
  bootAudio();
  if(clearTimer){ clearTimeout(clearTimer); clearTimer = undefined; }
  const seed = seedFromDate();
  history.replaceState(null,'',`?seed=${encodeURIComponent(seed)}`);
  game = createGame(seed);
  sound('tap');
  render('New game.');
}
async function share(){
  bootAudio(); sound('share');
  const final = game.finished ? game.totalScore : score();
  const msg = `Color Alchemy ${game.seed}\n${'★'.repeat(stars())}${'☆'.repeat(3-stars())} ${final} pts · best ${game.best}%\n${location.origin}/color-alchemy/?seed=${encodeURIComponent(game.seed)}`;
  try{
    if(navigator.share) await navigator.share({ title:'Color Alchemy', text:msg, url:`${location.origin}/color-alchemy/?seed=${encodeURIComponent(game.seed)}` });
    else await navigator.clipboard.writeText(msg);
    render('Share copied.');
  }catch{ render('Share cancelled.'); }
}

function render(message='Match the target color.'){
  const current = score();
  const status = game.lastResult || (game.finished ? `FINAL · ${game.totalScore} pts · Best ${game.best}%` : message);
  const effects = game.lastOutcome === 'clear' ? `<div class="sparkles" aria-hidden="true">${Array.from({length:12},(_,i)=>`<i style=\"--i:${i}\"></i>`).join('')}</div>` : '';
  app.innerHTML = `
    <section class="game ${game.finished?'is-finished':''} ${game.lastOutcome ? `is-${game.lastOutcome}` : ''}">
      ${effects}
      <header class="topbar">
        <button class="utility" id="newBtn">New</button>
        <div class="title"><b>Color Alchemy</b><span>Stage ${game.stage}/${MAX_STAGE} · ${game.totalScore} pts${game.combo ? ` · x${game.combo}` : ''}</span></div>
        <button class="utility" id="shareBtn">Share</button>
      </header>

      <section class="lab" aria-label="Color mixing board">
        <div class="scorePill">${game.finished ? 'DONE' : `${current}%`}</div>
        <div class="cups">
          <article class="cupBlock">
            <div class="cup target" style="--fill:${css(game.target)}"></div>
            <span>Target</span>
          </article>
          <article class="cupBlock mainCup">
            <div class="cup mix" style="--fill:${css(game.mix)}"></div>
            <span>Your mix</span>
          </article>
        </div>
        <div class="drops" aria-label="Drops used">${Array.from({length:MAX_DROPS},(_,i)=>`<i class="${i<game.drops?'used':''}"></i>`).join('')}</div>
        <p class="message ${game.lastResult ? `result ${game.lastOutcome}` : ''}">${game.finished ? `${'★'.repeat(stars())}${'☆'.repeat(3-stars())} ${game.totalScore} pts · Best ${game.best}%` : status}</p>
      </section>

      <section class="paints" aria-label="Paint buttons">
        ${paints.map((p,i)=>`
          <button class="paint paint-${p.key}" data-paint="${i}" style="--paint:${css(p.color)}" ${game.finished||!!clearTimer?'disabled':''}>
            <span class="blob"></span><strong>${p.label}</strong>
          </button>
        `).join('')}
      </section>

      <footer class="footer">
        <button class="utility wide" id="undoBtn">Undo</button>
        <p>Fewer drops = bonus. Consecutive CLEAR = combo.</p>
      </footer>
    </section>
  `;
  app.querySelector<HTMLButtonElement>('#newBtn')!.onclick = reset;
  app.querySelector<HTMLButtonElement>('#shareBtn')!.onclick = share;
  app.querySelector<HTMLButtonElement>('#undoBtn')!.onclick = undo;
  app.querySelectorAll<HTMLButtonElement>('.paint').forEach(btn => btn.onclick = () => addPaint(Number(btn.dataset.paint)));
}

render();
